<template>
	<Chart v-if="chartData" type="radar" :data="chartData" :options="chartOptions" class="w-full h-full" />
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue"
import Chart from "primevue/chart"

interface DatasetInput {
	label: string
	values: number[]
	color: string
}

const DEFAULT_CHART_FONT_SIZE = 14

const props = defineProps({
	labels: {
		type: Array as PropType<string[]>,
		required: true,
	},
	datasets: {
		type: Array as PropType<DatasetInput[]>,
		required: true,
	},
	axisMin: {
		type: Number,
		required: false,
		default: 0,
	},
	axisMax: {
		type: Number,
		required: false,
		default: undefined,
	},
	labelMaxLength: {
		type: Number,
		required: false,
		default: 24,
	},
	pointLabelFontSize: {
		type: Number,
		required: false,
		default: DEFAULT_CHART_FONT_SIZE,
	},
})

const COLORS = ["#4472C4", "#ED7D31", "#A5A5A5", "#FFC000", "#5B9BD5", "#70AD47", "#264478", "#9B57A0"]

const TARGET_TICK_COUNT = 4.5
const NICE_TICK_STEP_MULTIPLIERS = [1, 1.5, 2, 2.5, 5]

// Picks a round tick step (1, 2, 2.5 or 5 times a power of ten) that gives about 4-5 tick labels,
// so the scale numbers on a small radar do not overlap. On a tie, the step with fewer ticks wins.
function calculateTickStep(min: number, max: number | undefined): number | undefined {
	if (max === undefined || max <= min) return undefined
	const range = max - min
	const baseExponent = Math.floor(Math.log10(range / TARGET_TICK_COUNT))

	let bestStep: number | undefined
	let bestDistance = Number.POSITIVE_INFINITY
	for (let exponent = baseExponent - 1; exponent <= baseExponent + 1; exponent++) {
		for (const multiplier of NICE_TICK_STEP_MULTIPLIERS) {
			const step = multiplier * Math.pow(10, exponent)
			const tickCount = Math.floor(range / step) + 1
			const distance = Math.abs(tickCount - TARGET_TICK_COUNT)
			const isBetter = distance < bestDistance || (distance === bestDistance && step > (bestStep ?? 0))
			if (isBetter) {
				bestStep = step
				bestDistance = distance
			}
		}
	}
	return bestStep
}

function wrapRadarLabel(label: string, maxLength: number): string[] {
	const normalizedLabel = label.trim()
	const safeMaxLength = Math.max(1, maxLength)
	if (!normalizedLabel || normalizedLabel.length <= safeMaxLength) return [normalizedLabel]

	const lines: string[] = []
	let currentLine = ""

	for (const originalWord of normalizedLabel.split(/\s+/)) {
		let word = originalWord
		const candidate = currentLine ? `${currentLine} ${word}` : word
		if (candidate.length <= safeMaxLength) {
			currentLine = candidate
			continue
		}

		if (currentLine) {
			lines.push(currentLine)
			currentLine = ""
		}

		while (word.length > safeMaxLength) {
			lines.push(word.slice(0, safeMaxLength))
			word = word.slice(safeMaxLength)
		}

		currentLine = word
	}

	if (currentLine) lines.push(currentLine)
	return lines
}

const chartData = computed(() => {
	if (!props.labels?.length || !props.datasets?.length) return null

	return {
		labels: props.labels,
		datasets: props.datasets.map((ds, idx) => ({
			label: ds.label,
			data: ds.values,
			borderColor: ds.color || COLORS[idx % COLORS.length],
			backgroundColor: `${ds.color || COLORS[idx % COLORS.length]}33`,
			pointBackgroundColor: ds.color || COLORS[idx % COLORS.length],
			pointBorderColor: "#fff",
			pointHoverBackgroundColor: "#fff",
			pointHoverBorderColor: ds.color || COLORS[idx % COLORS.length],
			fill: true,
		})),
	}
})

const chartOptions = computed(() => ({
	responsive: true,
	maintainAspectRatio: false,
	layout: {
		padding: 8,
	},
	plugins: {
		legend: {
			position: "bottom" as const,
			labels: {
				usePointStyle: true,
				padding: 12,
				font: { size: DEFAULT_CHART_FONT_SIZE },
			},
		},
	},
	scales: {
		r: {
			min: props.axisMin ?? 0,
			max: props.axisMax,
			ticks: {
				display: true,
				stepSize: calculateTickStep(props.axisMin ?? 0, props.axisMax),
				font: { size: DEFAULT_CHART_FONT_SIZE },
			},
			pointLabels: {
				font: { size: props.pointLabelFontSize },
				padding: 6,
				callback: (label: string) => wrapRadarLabel(label, props.labelMaxLength),
			},
		},
	},
}))
</script>
